import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaCopy, FaCheck, FaInfoCircle } from "react-icons/fa";

interface NetworkConfig {
  label: string;
  value: string;
}

const NetworkConfigNotice: React.FC = () => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const networkConfig: NetworkConfig[] = [
    { label: "Network name", value: "Binance Smart Chain" },
    { label: "Default RPC URL", value: "https://binance.llamarpc.com" },
    { label: "Chain ID", value: "56" },
    { label: "Currency symbol", value: "BNB" },
    { label: "Block explorer URL", value: "https://bscscan.com" },
  ];

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full"
    >
      <div className="relative">
        {/* Gradient Border Effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg blur opacity-25"></div>

        <div className="relative bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-lg p-3 sm:p-4 shadow-lg">
          {/* Header - Mobile Optimized */}
          <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
            <FaInfoCircle className="text-yellow-600 text-base sm:text-lg lg:text-xl flex-shrink-0 mt-1 sm:mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 leading-tight">
                BSC Network Configuration Required
              </h3>
            </div>
          </div>

          {/* Description - Mobile Optimized */}
          <div className="mb-4 sm:mb-6">
            <p className="text-gray-700 leading-relaxed text-xs sm:text-sm lg:text-base">
              📢 Please configure your wallet with the following Binance Smart
              Chain settings for trading and converting BSC tokens to Hedera:
            </p>
          </div>

          {/* Network Configuration - Mobile First Design */}
          <div className="space-y-2 sm:space-y-3 lg:space-y-4">
            {networkConfig.map((config, index) => (
              <motion.div
                key={config.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-md sm:rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              >
                {/* Mobile: Compact vertical layout */}
                <div className="p-2 sm:p-3 lg:p-4">
                  <div className="flex flex-col space-y-2">
                    {/* Label */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium text-gray-600">
                        {config.label}:
                      </span>
                      <motion.button
                        onClick={() =>
                          copyToClipboard(config.value, config.label)
                        }
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded transition-colors duration-200 flex-shrink-0"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        title={`Copy ${config.label}`}
                      >
                        {copiedField === config.label ? (
                          <>
                            <FaCheck className="w-3 h-3 text-green-600" />
                            <span className="text-green-600">Copied!</span>
                          </>
                        ) : (
                          <>
                            <FaCopy className="w-3 h-3" />
                            <span>Copy</span>
                          </>
                        )}
                      </motion.button>
                    </div>

                    {/* Value */}
                    <div className="bg-gray-50 border rounded p-2 sm:p-3">
                      <code className="text-gray-900 text-xs sm:text-sm break-all font-mono leading-relaxed">
                        {config.value}
                      </code>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer Note - Mobile Optimized */}
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-md sm:rounded-lg">
            <div className="flex items-start gap-2">
              <span className="text-blue-600 text-sm sm:text-base lg:text-lg flex-shrink-0">
                💡
              </span>
              <div className="text-blue-800 text-xs sm:text-sm leading-relaxed flex-1 min-w-0">
                <p className="mb-2 sm:mb-3">
                  Learn how to add BSC network in your wallet:
                </p>
                <div className="space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:gap-4">
                  <a
                    className="inline-block text-blue-600 hover:text-blue-800 underline font-medium"
                    href="https://support.metamask.io/configure/networks/how-to-add-a-custom-network-rpc/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📱 MetaMask Guide
                  </a>
                  <a
                    className="inline-block text-blue-600 hover:text-blue-800 underline font-medium"
                    href="https://trustwallet.com/blog/guides/how-to-add-a-custom-network-on-the-trust-wallet-app"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🔐 TrustWallet Guide
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NetworkConfigNotice;
